import type { Highlight } from '../../api/types'
import { beschreibe, lageVon, type Lage } from '../../lib/wetterlage'
import {
  GewitterIcon,
  NebelIcon,
  RegenIcon,
  SchneeIcon,
  SonneIcon,
  TeilsIcon,
  WindIcon,
  WolkeIcon,
} from '../../components/Icons'

/**
 * **Die Nennung ist Pflicht, keine Höflichkeit** — Apple verlangt sie für
 * WeatherKit, samt Verweis auf die rechtlichen Hinweise. Sie steht deshalb
 * dort, wo das Wetter steht, und nicht irgendwo in einem Impressum.
 *
 * Als eigenes Bauteil, damit Adresse und Wortlaut an **einer** Stelle stehen:
 * das Wetter erscheint inzwischen an zwei Orten — im Blatt hinter der Karte
 * und als Zeile unter dem Kartenstapel.
 *
 * Geschrieben als „Apple Weather" und nicht mit dem Apfelzeichen: das ist ein
 * Zeichen aus Apples eigenem Bereich der Zeichentabelle und erscheint auf
 * Android und Windows als leeres Kästchen.
 */
export function WetterNennung({ className = 'zeitpunkt__quelle' }: { className?: string }) {
  return (
    <a
      className={className}
      href="https://weatherkit.apple.com/legal-attribution.html"
      target="_blank"
      rel="noreferrer"
    >
      Apple Weather
    </a>
  )
}

/** Das Zeichen zu einer Lage — oder nichts, wenn wir den Schlüssel nicht kennen. */
export function WetterZeichen({ lage, size = 15 }: { lage: Lage | null; size?: number }) {
  if (lage === null) return null
  const Bild = {
    klar: SonneIcon,
    teils: TeilsIcon,
    wolkig: WolkeIcon,
    regen: RegenIcon,
    schnee: SchneeIcon,
    gewitter: GewitterIcon,
    nebel: NebelIcon,
    wind: WindIcon,
  }[lage]
  return <Bild size={size} className="wetter__zeichen" />
}

/**
 * Das Wetter zu dem Zeitpunkt, an dem jemand dort war — in ganzer Länge.
 *
 * Zeichen, Temperatur, Beschreibung in Worten und die Nennung. Auf der Kachel
 * im Kartenstapel steht die knappe Variante (`WetterKurz`); dort ist Platz für
 * ein Zeichen und zwei Ziffern, mehr nicht.
 */
export function Wetter({ wetter }: { wetter: Highlight['weather'] }) {
  if (wetter === null) return null

  const grad = typeof wetter.temperature_c === 'number' ? Math.round(wetter.temperature_c) : null
  const code = typeof wetter.condition === 'string' ? wetter.condition : null
  const lage = code === null ? null : beschreibe(code)
  if (grad === null && lage === null) return null

  return (
    <p className="zeitpunkt__wetter">
      <WetterZeichen lage={code === null ? null : lageVon(code)} />
      {[grad === null ? null : `${grad} °C`, lage].filter(Boolean).join(', ')}
      <WetterNennung />
    </p>
  )
}

/**
 * Die knappe Variante für das Deckblatt: Zeichen und Temperatur.
 *
 * **Ohne Nennung** — die steht einmal unter dem Stapel und gilt für alle
 * Kacheln darauf. Auf einer Kachel von Daumengröße wäre sie größer als die
 * Angabe selbst; genau daran ist der erste Anlauf gescheitert, und deshalb
 * stand dort lange gar kein Wetter.
 */
export function WetterKurz({ wetter }: { wetter: Highlight['weather'] }) {
  if (wetter === null) return null

  const grad = typeof wetter.temperature_c === 'number' ? Math.round(wetter.temperature_c) : null
  const code = typeof wetter.condition === 'string' ? wetter.condition : null
  const lage = code === null ? null : lageVon(code)
  if (grad === null && lage === null) return null

  return (
    <span className="stapel__wetter">
      <WetterZeichen lage={lage} size={14} />
      {grad !== null && <span>{grad} °C</span>}
      {/* Für Vorlesegeräte und für den, der das Zeichen nicht deutet: das Wort
          steht da, sichtbar ist nur das Zeichen. */}
      {code !== null && <span className="visually-hidden">{beschreibe(code)}</span>}
    </span>
  )
}

/** Trägt dieser Punkt überhaupt eine Wetterangabe? */
export function hatWetter(wetter: Highlight['weather']): boolean {
  if (wetter === null) return false
  return typeof wetter.temperature_c === 'number' || typeof wetter.condition === 'string'
}
